import type { AgentPageTaskProgress, AgentPageTaskResult } from './page-task-protocol';
export type Presentation = 'full-page' | 'sidebar' | 'floating-window';
export type AgentUnreadResultKind = 'completed' | 'awaiting_confirmation' | 'failed';
export interface ComposerPredictionAdoption {
  messageId: string;
  acceptedText: string;
}
export interface AgentSession {
  id: string;
  title: string | null;
  source: string;
  status: string;
  writable: boolean;
  pinned: boolean;
  has_messages: boolean;
  has_unread_agent_result: boolean;
  unread_agent_result_kind?: AgentUnreadResultKind | null;
  created_at: string;
  last_active_at: string;
  last_message_at?: string | null;
}
export interface AgentFile {
  id: string;
  session_id: string;
  file_name: string;
  kind: 'UPLOAD' | 'GENERATED';
  status: 'PENDING' | 'READY';
  message_id?: string;
}
export interface ToolEvent {
  id: string;
  tool_call_id?: string | null;
  tool_name?: string | null;
  event_type: string;
  duration_ms?: number | null;
  event_payload?: unknown;
  created_at?: string;
  sequence?: number;
  status?: string | null;
}
export interface AgentQueryWait {
  waitId: string;
  agentMessageId: string;
  state: string;
  phase: string;
  messageStatus: string;
  pollAfterMs: number;
  canSendMessage: boolean;
  attemptId: string;
  jobStatus?: string | null;
  errorCode?: string | null;
}
export interface AgentMessage {
  id: string;
  role: 'user' | 'agent' | 'guidance';
  content: string;
  inputText?: string;
  pastedTexts?: { id: string; text: string }[];
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  created_at: string;
  reasoning?: string | null;
  reasoning_segments?: { sequence: number; end_sequence: number; content: string }[];
  thinking_started_at?: string | null;
  thinking_completed_at?: string | null;
  tool_events: ToolEvent[];
  attachments?: AgentFile[];
  ui_payload?: unknown;
  user_progress?: { label: string; summary: string; steps?: string[] } | null;
  page_task_result?: AgentPageTaskResult;
  query_wait?: AgentQueryWait | null;
}
export type AgentMessageDto = Omit<AgentMessage, 'status'> & {
  status: AgentMessage['status'] | 'waiting_query';
};
export interface NavigationAction {
  id: string;
  workspace_id: string;
  selection?: string;
}
export interface StreamEvent {
  event: string;
  payload: Record<string, unknown>;
}
export interface Run {
  messageId: string;
  sequence: number;
  eventSequence: number;
  progressSequence: number;
  reconnecting: boolean;
  queryWait?: AgentQueryWait;
  stopping?: boolean;
  attemptId?: string;
}
export interface Snapshot {
  sessions: AgentSession[];
  sessionsById: Record<string, AgentSession>;
  page: number;
  keyword: string;
  hasMore: boolean;
  uploading: number;
  listLoading: boolean;
  listError: boolean;
  selectedId?: string;
  messages: Record<string, AgentMessage[]>;
  drafts: Record<string, string>;
  pastedTexts: Record<string, { id: string; text: string }[]>;
  files: Record<string, AgentFile[]>;
  loading: Record<string, boolean>;
  errors: Record<string, boolean>;
  runs: Record<string, Run | undefined>;
  /** 仅记录本次运行成功收尾的回复，历史消息加载不触发输入预测。 */
  predictionMessageIds?: Record<string, string>;
  pageTask?: {
    sessionId: string;
    taskId: string;
    messageId?: string;
    status: 'running' | AgentPageTaskResult['status'];
    progress?: AgentPageTaskProgress;
    steps: AgentPageTaskProgress[];
    result?: AgentPageTaskResult;
  };
}

export interface AgentStarter {
  id: string;
  content: string;
  icon: string;
}
export interface AgentFeedbackCategory {
  id: number;
  name: string;
  enabled: boolean;
}
export interface AgentFeedback {
  userVoiceCaseId?: number | null;
  userVoiceSyncStatus?: 'PENDING' | 'SYNCED' | null;
  messagePublicId: string;
  rating: 'UP' | 'DOWN';
  categoryIds: number[];
  comment: string;
}

export function isAutomationSessionSource(source?: string): boolean {
  return source === 'AUTOMATION' || source === 'AUTOMATION_RUN';
}

/** 浮窗与侧栏承接交互会话，任务记录只在完整页展示；来源未加载时暂时保留。 */
export function isSessionAvailableInCompactMode(
  session: Pick<AgentSession, 'source'> | undefined,
): boolean {
  return !isAutomationSessionSource(session?.source);
}

/** 查询等待保留运行锁，但只在 RESUMING 阶段恢复正文与思考的流式展示。 */
export function isAgentMessageStreaming(
  message: Pick<AgentMessage, 'status' | 'query_wait'>,
): boolean {
  return (
    message.status === 'running' && (!message.query_wait || message.query_wait.state === 'RESUMING')
  );
}
